import { FC, MouseEventHandler, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { SVG_MUI } from "@ryde/assets";
import { useRtl, useTableFiltersActions } from "@ryde/redux";
import {
  IDropdownItem,
  Log,
  stringifyDropdownItems,
  useIsMobile,
} from "@ryde/shared";

import { FramerTooltip } from "../../common";
import { MenuButton } from "../../controls";
import { useFilterMultiselectBound } from "./hooks";
import {
  FilterDropdownWrapper,
  IFilterMultiProps,
  selectStyles,
  selectStylesMobile,
  StyledLabelBlack,
  StyledLabelBlue,
} from "./parts";
import { MemoSelect } from "./parts/memo-select";

export const MAX_FILTER_ITEMS = 5;

interface ITooltipWrapperProps {
  items: IDropdownItem[];
  filteredLabel: ReactNode;
}

const TooltipWrapper: FC<ITooltipWrapperProps> = ({ items, filteredLabel }) => {
  const { direction } = useRtl();

  if (items.length <= MAX_FILTER_ITEMS) {
    return (
      <span>
        {filteredLabel}:{stringifyDropdownItems(items)}
      </span>
    );
  }

  return (
    <span dir={direction}>
      <span dir={direction}>
        {filteredLabel}:{" "}
        {stringifyDropdownItems(items.slice(0, MAX_FILTER_ITEMS))}
      </span>
      <span dir={direction}> +{items.length - MAX_FILTER_ITEMS}</span>
    </span>
  );
};

export const FilterMultiSelectBound: FC<IFilterMultiProps> = props => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const {
    value,
    buttonContent,
    multiselectLabel,
    onItemsCheckChange,
    dropdownItems,
    isOpen,
    setIsOpen,
    selectAll,
    isSelectedAll,
    input,
    onInputChange,
    filteredDropDownItems,
    toggleFilterSelectNoneValue,
    isSelectNoneValueActive,
    isSelectNoneOptionEnabled,
  } = useFilterMultiselectBound(props);
  const { customFieldId } = props;

  const handleToggle = useEvent(() => setIsOpen(prev => !prev));
  const handleClose = useEvent(() => setIsOpen(false));
  const { handleRemoveFilter } = useTableFiltersActions();
  const removeFilter: MouseEventHandler = useEvent(e => {
    e.stopPropagation();
    handleClose();

    if (customFieldId) {
      handleRemoveFilter(+customFieldId);
    } else {
      Log.warn(t("errors.cantRemoveFilterInvalidFieldId"));
    }
  });

  const stylesComputed = useMemo(() => {
    return isMobile ? selectStylesMobile : selectStyles;
  }, [isMobile]);

  return (
    <FilterDropdownWrapper
      dropdownHasSelectedItems={!!value?.length}
      isFilterItem={true}
      isOpen={isOpen}
      onClose={handleClose}
      target={
        <FramerTooltip
          content={
            <TooltipWrapper
              items={value ?? []}
              filteredLabel={t("general.FILTERED_BY_ENTITY", {
                entity: multiselectLabel,
              })}
            />
          }
        >
          <MenuButton
            onClick={handleToggle}
            text={multiselectLabel}
            labelBlack={buttonContent.labelBlack}
            labelBlue={buttonContent.labelBlue}
            imageAfterText
            disabled={!dropdownItems.length && !isSelectNoneOptionEnabled}
          >
            <StyledLabelBlack>{buttonContent.labelBlack}</StyledLabelBlack>
            <StyledLabelBlue>{buttonContent.labelBlue}</StyledLabelBlue>
            <SVG_MUI.CircleXMark onClick={e => removeFilter(e)} />
          </MenuButton>
        </FramerTooltip>
      }
    >
      <MemoSelect
        value={value}
        selectAll={selectAll}
        isSelectedAll={isSelectedAll}
        filteredDropDownItems={filteredDropDownItems}
        dropdownItems={dropdownItems}
        onItemsCheckChange={onItemsCheckChange}
        stylesComputed={stylesComputed}
        input={input}
        onInputChange={onInputChange}
        isSelectNoneOptionEnabled={isSelectNoneOptionEnabled}
        toggleFilterSelectNoneValue={toggleFilterSelectNoneValue}
        isSelectNoneValueActive={isSelectNoneValueActive}
      />
    </FilterDropdownWrapper>
  );
};
